import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded'
import { useParams } from 'react-router-dom'
import Logo from '../components/Logo'
import RegistrationFieldInput from '../components/RegistrationFieldInput'
import { LoadingState } from '../components/ui/States'
import { resolveAssetUrl } from '../services/assetUrl'
import {
  publicRegistrationService,
  type PublicRegistrationInfo,
} from '../services/registrationService'
import { formatDateRange } from '../utils/format'

type Values = Record<string, string>

function apiMessage(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response
    ?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

/**
 * Public, unauthenticated self-service registration page (the HubSpot
 * replacement). Lives OUTSIDE the attendee auth gate. Anyone with the link can
 * register; on submit we show a "confirm your email" state. After they confirm
 * and an admin approves, they receive a sign-in link that opens the app.
 */
export default function PublicRegisterPage() {
  const { slug = '' } = useParams()
  const [info, setInfo] = useState<PublicRegistrationInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [notAvailable, setNotAvailable] = useState(false)

  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [company, setCompany] = useState('')
  const [code, setCode] = useState('')
  const [values, setValues] = useState<Values>({})
  const [honeypot, setHoneypot] = useState('') // must stay empty
  const [errors, setErrors] = useState<Record<string, boolean>>({})
  const [emailError, setEmailError] = useState(false)
  const [codeError, setCodeError] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    publicRegistrationService
      .getInfo(slug)
      .then(setInfo)
      .catch(() => setNotAvailable(true))
      .finally(() => setLoading(false))
  }, [slug])

  const requiredFields = useMemo(
    () => (info?.fields ?? []).filter((f) => f.required),
    [info],
  )

  const setValue = (fieldId: string, value: string) => {
    setValues((v) => ({ ...v, [fieldId]: value }))
    setErrors((e) => ({ ...e, [fieldId]: false }))
  }

  const handleSubmit = async () => {
    if (!info) return
    const nextErrors: Record<string, boolean> = {}
    for (const f of requiredFields) {
      const v = values[f.id]
      if (v === undefined || v === '') nextErrors[f.id] = true
    }
    const badEmail = !email.trim() || !email.includes('@')
    setEmailError(badEmail)
    const badCode = info.registrationCodeRequired && !code.trim()
    setCodeError(badCode)
    setErrors(nextErrors)
    if (badEmail || badCode || Object.keys(nextErrors).length > 0) return

    setSubmitting(true)
    setError(null)
    try {
      await publicRegistrationService.register(slug, {
        email: email.trim(),
        fullName: fullName.trim() || undefined,
        company: company.trim() || undefined,
        values,
        formToken: info.formToken,
        website: honeypot,
        registrationCode: info.registrationCodeRequired
          ? code.trim()
          : undefined,
      })
      setSubmitted(true)
    } catch (e) {
      setError(apiMessage(e, 'Something went wrong. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <LoadingState />

  if (notAvailable || !info) {
    return (
      <Shell>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Logo />
          <Typography variant="h6" fontWeight={700}>
            Registration isn't available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This event isn't open for registration right now. Please check the
            link, or contact your Vericast event manager.
          </Typography>
        </Stack>
      </Shell>
    )
  }

  if (submitted) {
    return (
      <Shell heroImage={info.heroImage}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,117,188,0.12)',
              color: 'primary.main',
            }}
          >
            <MarkEmailReadRoundedIcon />
          </Box>
          <Typography variant="h5" fontWeight={700}>
            Check your email
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We've sent a confirmation link to <strong>{email}</strong>. Open it
            to confirm your registration for {info.name}. Once our team approves
            it, you'll get a link to access the event app.
          </Typography>
        </Stack>
      </Shell>
    )
  }

  return (
    <Shell heroImage={info.heroImage}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Logo />
        <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
          Register for {info.name}
        </Typography>
        {info.tagline && (
          <Typography variant="body1" color="text.secondary">
            {info.tagline}
          </Typography>
        )}
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {info.startsAt && info.endsAt && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarMonthRoundedIcon fontSize="small" color="action" />
              <Typography variant="body2" fontWeight={600}>
                {formatDateRange(info.startsAt, info.endsAt)}
              </Typography>
            </Stack>
          )}
          {info.location && (
            <Stack direction="row" spacing={1} alignItems="center">
              <PlaceRoundedIcon fontSize="small" color="action" />
              <Typography variant="body2" fontWeight={600}>
                {info.location}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        <TextField
          label={
            <>
              Email address<span style={{ color: '#d32f2f' }}> *</span>
            </>
          }
          type="email"
          fullWidth
          value={email}
          error={emailError}
          helperText={emailError ? 'Enter a valid email address.' : undefined}
          onChange={(e) => {
            setEmail(e.target.value)
            setEmailError(false)
          }}
          autoComplete="email"
        />
        <TextField
          label="Full name"
          fullWidth
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoComplete="name"
        />
        <TextField
          label="Company"
          fullWidth
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          autoComplete="organization"
        />

        {info.registrationCodeRequired && (
          <TextField
            label={
              <>
                Invitation code<span style={{ color: '#d32f2f' }}> *</span>
              </>
            }
            fullWidth
            value={code}
            error={codeError}
            helperText={
              codeError
                ? 'An invitation code is required to register for this event.'
                : 'Enter the code provided by your event organizer.'
            }
            onChange={(e) => {
              setCode(e.target.value)
              setCodeError(false)
            }}
            autoComplete="off"
          />
        )}

        {info.fields.map((field) => (
          <RegistrationFieldInput
            key={field.id}
            field={field}
            value={values[field.id]}
            error={errors[field.id]}
            onChange={(v) => setValue(field.id, v)}
          />
        ))}

        {/* Honeypot: hidden from users; bots that fill it are silently dropped. */}
        <TextField
          label="Website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          sx={{ display: 'none' }}
        />

        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : 'Register'}
        </Button>
        <Typography variant="caption" color="text.secondary" textAlign="center">
          We'll email you to confirm your address. Access to the event app is
          granted after your registration is approved.
        </Typography>
      </Stack>
    </Shell>
  )
}

/** Centered card layout with an optional hero image banner. */
function Shell({
  children,
  heroImage,
}: {
  children: React.ReactNode
  heroImage?: string | null
}) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: { xs: 4, md: 8 },
        px: 2,
      }}
    >
      <Container maxWidth="sm" disableGutters>
        <Card variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
          {heroImage && (
            <Box
              sx={{
                height: 160,
                backgroundImage: `url(${resolveAssetUrl(heroImage)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          )}
          <Box sx={{ p: { xs: 3, md: 4 } }}>{children}</Box>
        </Card>
      </Container>
    </Box>
  )
}
